import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTelegramStatus, generateTelegramLink, unlinkTelegram, getTelegramHistory } from '../services/settingsApi';

function TelegramPage() {
  const [telegramLinked, setTelegramLinked] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState(null);
  const [telegramDeepLink, setTelegramDeepLink] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramError, setTelegramError] = useState(null);
  const [telegramSuccess, setTelegramSuccess] = useState(null);

  const [messages, setMessages] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const HISTORY_LIMIT = 50;

  const [statusLoading, setStatusLoading] = useState(true);
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadTelegramStatus();
  }, []);

  const loadHistory = useCallback(async (offset = 0, append = false) => {
    try {
      setHistoryLoading(true);
      const res = await getTelegramHistory(HISTORY_LIMIT, offset);
      if (append) {
        setMessages(prev => [...res.messages, ...prev]);
      } else {
        setMessages(res.messages || []);
      }
      setHistoryTotal(res.total || 0);
      setHistoryOffset(offset);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadTelegramStatus = async () => {
    try {
      setStatusLoading(true);
      const res = await getTelegramStatus();
      setTelegramLinked(res.linked);
      setTelegramUsername(res.telegramUsername || null);
      if (res.linked) {
        await loadHistory(0);
      }
    } catch (err) {
      console.error('Error loading Telegram status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    if (messages.length > 0 && historyOffset === 0) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, historyOffset]);

  const handleConnectTelegram = async () => {
    try {
      setTelegramLoading(true);
      setTelegramError(null);
      setTelegramDeepLink(null);

      const res = await generateTelegramLink();
      if (res.deepLink) {
        setTelegramDeepLink(res.deepLink);
        window.open(res.deepLink, '_blank');
        const poll = setInterval(async () => {
          try {
            const status = await getTelegramStatus();
            if (status.linked) {
              clearInterval(poll);
              setTelegramLinked(true);
              setTelegramUsername(status.telegramUsername);
              setTelegramDeepLink(null);
              setTelegramSuccess('Telegram connected successfully!');
              setTimeout(() => setTelegramSuccess(null), 5000);
              loadHistory(0);
            }
          } catch {}
        }, 3000);
        setTimeout(() => clearInterval(poll), 10 * 60 * 1000);
      } else {
        setTelegramError('Bot name not configured. Please set TELEGRAM_BOT_NAME in your environment.');
      }
    } catch (err) {
      setTelegramError(err.message);
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleDisconnectTelegram = async () => {
    try {
      setTelegramLoading(true);
      setTelegramError(null);
      await unlinkTelegram();
      setTelegramLinked(false);
      setTelegramUsername(null);
      setMessages([]);
      setTelegramSuccess('Telegram disconnected.');
      setTimeout(() => setTelegramSuccess(null), 3000);
    } catch (err) {
      setTelegramError(err.message);
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleLoadMore = () => {
    const newOffset = historyOffset + HISTORY_LIMIT;
    loadHistory(newOffset, true);
  };

  const handleRefresh = () => {
    loadHistory(0, false);
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    if (isYesterday) return `Yesterday ${time}`;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'photo': return '📷';
      case 'document': return '📎';
      default: return null;
    }
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '800px',
      margin: '0 auto',
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      marginBottom: 'var(--space-6)',
      color: 'var(--color-text-primary)',
      letterSpacing: '-0.025em',
    },
    section: {
      backgroundColor: 'var(--color-bg-base)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      marginBottom: 'var(--space-6)',
      boxShadow: 'var(--shadow-sm)',
      border: '1px solid var(--color-border-light)',
    },
    sectionTitle: {
      fontSize: 'var(--font-size-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      marginBottom: 'var(--space-4)',
      color: 'var(--color-text-primary)',
    },
    errorBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-error-light)',
      border: '1px solid var(--color-error)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-error-dark)',
    },
    successBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-success-light)',
      border: '1px solid var(--color-success)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-success)',
    },
    infoBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-info-light)',
      border: '1px solid var(--color-border-default)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-info)',
    },
    buttonGroup: {
      display: 'flex',
      gap: 'var(--space-3)',
      flexWrap: 'wrap',
    },
    primaryBtn: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-default)',
      border: 'none',
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-text-inverse)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    secondaryBtn: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-default)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    chatContainer: {
      maxHeight: '600px',
      overflowY: 'auto',
      padding: 'var(--space-4)',
      backgroundColor: '#F0F2F5',
      borderRadius: 'var(--radius-default)',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    messageBubble: {
      maxWidth: '75%',
      padding: '10px 14px',
      borderRadius: '14px',
      fontSize: 'var(--font-size-sm)',
      lineHeight: '1.45',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
    },
    incomingBubble: {
      alignSelf: 'flex-end',
      backgroundColor: '#DCF8C6',
      borderBottomRightRadius: '4px',
      color: '#111827',
    },
    outgoingBubble: {
      alignSelf: 'flex-start',
      backgroundColor: '#FFFFFF',
      borderBottomLeftRadius: '4px',
      color: '#111827',
      border: '1px solid #E8E8E8',
    },
    messageTime: {
      fontSize: '11px',
      color: '#71717A',
      marginTop: '4px',
    },
    messageFile: {
      fontSize: '12px',
      color: '#71717A',
      fontStyle: 'italic',
    },
    emptyChat: {
      textAlign: 'center',
      padding: 'var(--space-8)',
      color: 'var(--color-text-tertiary)',
      fontSize: 'var(--font-size-sm)',
    },
    loadMoreBtn: {
      alignSelf: 'center',
      padding: '6px 16px',
      borderRadius: '16px',
      border: 'none',
      backgroundColor: 'rgba(0,0,0,0.06)',
      color: '#71717A',
      fontSize: '12px',
      cursor: 'pointer',
      marginBottom: '8px',
    },
    chatHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 'var(--space-3)',
    },
    refreshBtn: {
      padding: '4px 12px',
      borderRadius: 'var(--radius-default)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-secondary)',
      fontSize: '12px',
      cursor: 'pointer',
    },
  };

  if (statusLoading) {
    return (
      <div style={styles.container}>
        <h2 style={styles.title}>Telegram</h2>
        <div style={styles.section}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Telegram</h2>

      {/* Connection Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Connection</h3>

        {telegramLinked ? (
          <div>
            <div style={{ ...styles.successBox, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>
                Connected{telegramUsername ? ` as @${telegramUsername}` : ''}
              </span>
            </div>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: '1.5' }}>
              You can send expenses, receipts, bank statements, and questions directly through Telegram.
            </p>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: '1.5' }}>
              Connect your Telegram account to log expenses, upload receipts, and ask questions directly from Telegram.
            </p>
            {telegramDeepLink && (
              <div style={styles.infoBox}>
                A link was opened in a new tab. If it didn't open, <a href={telegramDeepLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>click here</a>.
                Waiting for you to press Start in Telegram...
              </div>
            )}
          </div>
        )}

        {telegramError && <div style={styles.errorBox}>{telegramError}</div>}
        {telegramSuccess && <div style={styles.successBox}>{telegramSuccess}</div>}

        <div style={styles.buttonGroup}>
          {telegramLinked ? (
            <button
              onClick={handleDisconnectTelegram}
              disabled={telegramLoading}
              style={{
                ...styles.secondaryBtn,
                cursor: telegramLoading ? 'not-allowed' : 'pointer',
                opacity: telegramLoading ? 0.6 : 1,
                color: 'var(--color-error)',
                borderColor: 'var(--color-error)',
              }}
            >
              {telegramLoading ? 'Disconnecting...' : 'Disconnect Telegram'}
            </button>
          ) : (
            <button
              onClick={handleConnectTelegram}
              disabled={telegramLoading}
              style={{
                ...styles.primaryBtn,
                cursor: telegramLoading ? 'not-allowed' : 'pointer',
                opacity: telegramLoading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!telegramLoading) e.target.style.backgroundColor = 'var(--color-primary-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary)';
              }}
            >
              {telegramLoading ? 'Connecting...' : 'Connect Telegram'}
            </button>
          )}
        </div>
      </div>

      {/* Conversation History Section */}
      {telegramLinked && (
        <div style={styles.section}>
          <div style={styles.chatHeader}>
            <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Conversation History</h3>
            <button
              onClick={handleRefresh}
              disabled={historyLoading}
              style={styles.refreshBtn}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-bg-muted)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--color-bg-base)'}
            >
              {historyLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          <div style={styles.chatContainer}>
            {messages.length === 0 && !historyLoading && (
              <div style={styles.emptyChat}>
                <p style={{ fontSize: '32px', marginBottom: '8px' }}>💬</p>
                <p>No messages yet. Start chatting with the bot on Telegram!</p>
              </div>
            )}

            {historyTotal > messages.length && (
              <button
                onClick={handleLoadMore}
                disabled={historyLoading}
                style={styles.loadMoreBtn}
              >
                {historyLoading ? 'Loading...' : `Load older messages (${historyTotal - messages.length} more)`}
              </button>
            )}

            {messages.map((msg) => {
              const isIncoming = msg.direction === 'incoming';
              const icon = getMessageIcon(msg.message_type);
              return (
                <div
                  key={msg.id}
                  style={{
                    ...styles.messageBubble,
                    ...(isIncoming ? styles.incomingBubble : styles.outgoingBubble),
                  }}
                >
                  {!isIncoming && (
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#3B82F6', marginBottom: '2px' }}>
                      Bot
                    </div>
                  )}
                  {icon && <span style={{ marginRight: '4px' }}>{icon}</span>}
                  {msg.message_type !== 'text' && msg.file_name && (
                    <div style={styles.messageFile}>{msg.file_name}</div>
                  )}
                  {msg.message_text && <div>{msg.message_text}</div>}
                  {!msg.message_text && msg.message_type === 'photo' && (
                    <div style={styles.messageFile}>Photo</div>
                  )}
                  <div style={{
                    ...styles.messageTime,
                    textAlign: isIncoming ? 'right' : 'left',
                  }}>
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}

export default TelegramPage;
